import { Component } from 'react';
import { useParams } from "react-router-dom";
import { Footer, HeaderModern, Loader, TopBar, TopHeader } from '../layouts/general';
import { API_BASE, getImageUrl } from '../../config/api';
import SEO, { buildAbsoluteUrl, truncateDescription } from '../seo/SEO';

const defaultLogo = (typeof process.env.PUBLIC_URL === 'string' ? process.env.PUBLIC_URL : '') + '/images/logo@2x.png';

const TeamMemberDetailWrapper = (props) => {
    const params = useParams();
    return <TeamMemberDetail {...props} params={params} />;
};

class TeamMemberDetail extends Component {
    constructor(props) {
        super(props);
        this.state = {
            headers: [{ id: 1, names: 'Team Member' }],
            member: null,
            loading: true
        };
    }

    componentDidMount() {
        this.fetchMember();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.params?.id !== this.props.params?.id) {
            this.setState({ loading: true, member: null });
            this.fetchMember();
        }
    }

    fetchMember = () => {
        const { id } = this.props.params || {};
        if (!id) {
            this.setState({ loading: false });
            return;
        }
        fetch(`${API_BASE}/team/${id}`)
            .then(res => res.ok ? res.json() : null)
            .then(member => this.setState({ member, loading: false }))
            .catch(() => this.setState({ member: null, loading: false }));
    };

    renderSocials(member) {
        const hasSocials = member.email || member.linkedin || member.twitter;
        if (!hasSocials) return null;

        return (
            <div className="modern-profile-socials">
                {member.email && (
                    <a href={`mailto:${member.email}`} title={`Email ${member.name}`}>
                        <i className="fa fa-envelope" aria-hidden="true"></i>
                    </a>
                )}
                {member.linkedin && (
                    <a href={member.linkedin} title={`LinkedIn ${member.name}`} target="_blank" rel="noopener noreferrer">
                        <i className="fa fa-linkedin" aria-hidden="true"></i>
                    </a>
                )}
                {member.twitter && (
                    <a href={member.twitter} title={`Twitter ${member.name}`} target="_blank" rel="noopener noreferrer">
                        <i className="fa fa-twitter" aria-hidden="true"></i>
                    </a>
                )}
            </div>
        );
    }

    renderProfile(member) {
        const role = member.subtitle || member.title;
        const firstName = member.name ? member.name.split(' ')[0] : 'them';

        return (
            <section className="modern-team-detail-page">
                <div className="container">
                    <div className="modern-team-detail-profile">
                        <div className="modern-profile-avatar modern-team-detail-avatar">
                            <img
                                src={getImageUrl(member.image) || defaultLogo}
                                alt={member.name}
                                onError={(e) => { e.target.src = defaultLogo; }}
                            />
                        </div>
                        {role && <span className="modern-profile-role">{role}</span>}
                        <h1>{member.name}</h1>
                        {member.bio && <p className="modern-team-detail-bio">{member.bio}</p>}
                        {this.renderSocials(member)}
                        {member.email && (
                            <a href={`mailto:${member.email}`} className="modern-team-detail-contact">
                                Contact {firstName}
                            </a>
                        )}
                    </div>
                </div>
            </section>
        );
    }

    render() {
        const { member, loading } = this.state;

        if (loading) {
            return (
                <div className="bg-body3">
                    <div className="boxed">
                        <Loader />
                        <TopBar />
                        <TopHeader />
                        <section className="modern-team-detail-page">
                            <div className="container">
                                <p className="modern-team-detail-status">Loading…</p>
                            </div>
                        </section>
                        <Footer />
                    </div>
                </div>
            );
        }

        if (!member) {
            return (
                <div className="bg-body3">
                    <div className="boxed">
                        <TopBar />
                        <TopHeader />
                        <section className="modern-team-detail-page">
                            <div className="container">
                                <div className="modern-team-detail-profile">
                                    <h1>Team Member Not Found</h1>
                                </div>
                            </div>
                        </section>
                        <Footer />
                    </div>
                </div>
            );
        }

        return (
            <div className="bg-body3">
                <div className="boxed">
                    <SEO
                        title={member.metaTitle || `${member.name} | Stats Elite Team`}
                        description={member.metaDescription || member.bio || `${member.name} is part of the Stats Elite data consulting, MERL, statistics, and analytics team.`}
                        path={`/team/${this.props.params?.id || ''}`}
                        image={getImageUrl(member.image) || undefined}
                        schema={{
                            '@context': 'https://schema.org',
                            '@type': 'Person',
                            name: member.name,
                            jobTitle: member.subtitle || member.title || undefined,
                            description: truncateDescription(member.bio || ''),
                            image: getImageUrl(member.image) || undefined,
                            worksFor: { '@type': 'Organization', name: 'Stats Elite', url: buildAbsoluteUrl('/') },
                            email: member.email || undefined,
                            sameAs: [member.linkedin, member.twitter].filter(Boolean)
                        }}
                    />
                    <Loader />
                    <TopBar />
                    <TopHeader />
                    {
                        this.state.headers.map(data => (
                            <HeaderModern data={{ ...data, names: member.name }} key={data.id} />
                        ))
                    }

                    {this.renderProfile(member)}

                    <Footer />
                </div>
            </div>
        );
    }
}

export default TeamMemberDetailWrapper;
